import {
  BadgeBlockConfig,
  ButtonBlockConfig,
  DividerBlockConfig,
  GroupBlockConfig,
  HeadingBlockConfig,
  ImageBlockConfig,
  SectionBlockConfig,
  SpacerBlockConfig,
  TextBlockConfig,
} from "../components/primitives/primitives.config";
import {
  BadgeBlockFields,
  ButtonBlockFields,
  DividerBlockFields,
  GroupBlockFields,
  HeadingBlockFields,
  ImageBlockFields,
  SectionBlockFields,
  SpacerBlockFields,
  TextBlockFields,
} from "../components/primitives/primitives.fields";
import type { ComponentConfig, Config } from "@puckeditor/core";
import { BeforeAfterConfig } from "../components/before-after/before-after.config";
import { BeforeAfterFields } from "../components/before-after/before-after.fields";
import { BenefitsListConfig } from "../components/benefits-list/benefits-list.config";
import { BenefitsListFields } from "../components/benefits-list/benefits-list.fields";
import { BookingWidgetConfig } from "../components/booking-widget/booking-widget.config";
import { BookingWidgetFields } from "../components/booking-widget/booking-widget.fields";
import { BrandMarqueeConfig } from "../components/brand-marquee/brand-marquee.config";
import { BrandMarqueeFields } from "../components/brand-marquee/brand-marquee.fields";
import { BreadcrumbConfig } from "../components/breadcrumb/breadcrumb.config";
import { BreadcrumbFields } from "../components/breadcrumb/breadcrumb.fields";
import { CTAConfig } from "../components/cta/cta.config";
import { CTAFields } from "../components/cta/cta.fields";
import { ContactFormConfig } from "../components/contact-form/contact-form.config";
import { ContactFormFields } from "../components/contact-form/contact-form.fields";
import { ContentBlockConfig } from "../components/content-block/content-block.config";
import { ContentBlockFields } from "../components/content-block/content-block.fields";
import { EmergencyBandConfig } from "../components/emergency-band/emergency-band.config";
import { EmergencyBandFields } from "../components/emergency-band/emergency-band.fields";
import { FAQAccordionConfig } from "../components/faq-accordion/faq-accordion.config";
import { FAQAccordionFields } from "../components/faq-accordion/faq-accordion.fields";
import { FooterFields } from "../components/footer/footer.fields";
import { FormBlockConfig } from "../components/form-block/form-block.config";
import { FormBlockFields } from "../components/form-block/form-block.fields";
import { GalleryConfig } from "../components/gallery/gallery.config";
import { GalleryFields } from "../components/gallery/gallery.fields";
import { HeroConfig } from "../components/hero/hero.config";
import { HeroFields } from "../components/hero/hero.fields";
import { LinksPageConfig } from "../components/links-page/links-page.config";
import { LinksPageFields } from "../components/links-page/links-page.fields";
import { MobileStickyCallConfig } from "../components/mobile-sticky-call/mobile-sticky-call.config";
import { MobileStickyCallFields } from "../components/mobile-sticky-call/mobile-sticky-call.fields";
import { NavigationFields } from "../components/navigation/navigation.fields";
import { PricingInfoConfig } from "../components/pricing-info/pricing-info.config";
import { PricingInfoFields } from "../components/pricing-info/pricing-info.fields";
import { ProcessStepsConfig } from "../components/process-steps/process-steps.config";
import { ProcessStepsFields } from "../components/process-steps/process-steps.fields";
import { ReviewsConfig } from "../components/reviews/reviews.config";
import { ReviewsFields } from "../components/reviews/reviews.fields";
import { ServiceAreasConfig } from "../components/service-areas/service-areas.config";
import { ServiceAreasFields } from "../components/service-areas/service-areas.fields";
import { ServicesConfig } from "../components/services/services.config";
import { ServicesFields } from "../components/services/services.fields";
import { VideoConfig } from "../components/video/video.config";
import { VideoFields } from "../components/video/video.fields";

interface RegisteredJobsiteComponent {
  fields: Record<string, unknown>;
  editorConfig?: unknown;
  /**
   * Billing feature slug required to ADD this component to a page. The entry
   * declares it; pickers show the lock and the save path enforces it. Absent
   * means available on every plan. Moving a component in or out of a gate is
   * this one line.
   */
  requiredFeature?: string;
}

type RegisteredJobsiteComponentMap = Record<string, RegisteredJobsiteComponent>;

type FieldRegistryFrom<T extends RegisteredJobsiteComponentMap> = {
  [K in keyof T]: T[K]["fields"];
};

type EditorRegistryFrom<T extends RegisteredJobsiteComponentMap> = {
  [K in keyof T as T[K] extends { editorConfig: unknown } ? K : never]: T[K] extends {
    editorConfig: infer C;
  }
    ? NonNullable<C>
    : never;
};

/** Each editor component's props, read from its config so a new section is registered in one place. */
type JobsitePageComponentProps = {
  [K in keyof EditorRegistry]: EditorRegistry[K] extends ComponentConfig<infer P> ? P : never;
};
type EditorRegistry = EditorRegistryFrom<typeof JobsiteComponentRegistry>;

const buildEditorComponentRegistry = <T extends RegisteredJobsiteComponentMap>(
    registry: T,
  ): EditorRegistryFrom<T> => Object.fromEntries(
    Object.entries(registry)
      .filter(([, definition]) => definition.editorConfig !== undefined)
      .map(([name, definition]) => [name, definition.editorConfig]),
  ) as EditorRegistryFrom<T>,
  buildFieldRegistry = <T extends RegisteredJobsiteComponentMap>(
    registry: T,
  ): FieldRegistryFrom<T> => Object.fromEntries(
    Object.entries(registry).map(([name, definition]) => [name, definition.fields]),
  ) as FieldRegistryFrom<T>,

  /**
   * Authoritative registration surface for jobsite components.
   *
   * Fields drive generation, hydration, validation, and AI/editor introspection.
   * Optional editorConfig marks components that should appear in the Puck editor.
   */
  JobsiteComponentRegistry = {
    Hero: { fields: HeroFields, editorConfig: HeroConfig },
    CTA: { fields: CTAFields, editorConfig: CTAConfig },
    Services: { fields: ServicesFields, editorConfig: ServicesConfig },
    Reviews: { fields: ReviewsFields, editorConfig: ReviewsConfig },
    ContactForm: { fields: ContactFormFields, editorConfig: ContactFormConfig },
    FormBlock: { fields: FormBlockFields, editorConfig: FormBlockConfig },
    BookingWidget: { fields: BookingWidgetFields, editorConfig: BookingWidgetConfig },
    ContentBlock: { fields: ContentBlockFields, editorConfig: ContentBlockConfig },
    Navigation: { fields: NavigationFields },
    Footer: { fields: FooterFields },
    ProcessSteps: { fields: ProcessStepsFields, editorConfig: ProcessStepsConfig },
    FAQAccordion: { fields: FAQAccordionFields, editorConfig: FAQAccordionConfig },
    ServiceAreas: { fields: ServiceAreasFields, editorConfig: ServiceAreasConfig },
    Breadcrumb: { fields: BreadcrumbFields, editorConfig: BreadcrumbConfig },
    BenefitsList: { fields: BenefitsListFields, editorConfig: BenefitsListConfig },
    PricingInfo: { fields: PricingInfoFields, editorConfig: PricingInfoConfig },
    MobileStickyCall: {
      fields: MobileStickyCallFields,
      editorConfig: MobileStickyCallConfig,
    },
    Gallery: { fields: GalleryFields, editorConfig: GalleryConfig },
    // The links page's single component. Registered so it renders in the editor
    // And the static build, but hidden from every picker (puck-config category
    // + Simple UI exclusion) — it exists only on the /links page kind, whose
    // Permissions lock structure editing.
    LinksPage: { fields: LinksPageFields, editorConfig: LinksPageConfig },
    // Comparison slider gated like the Gallery's old before-after mode was:
    // Paid plans add it, existing placements stay editable on any plan.
    BeforeAfter: {
      fields: BeforeAfterFields,
      editorConfig: BeforeAfterConfig,
      requiredFeature: "advanced_layout_editing",
    },
    BrandMarquee: { fields: BrandMarqueeFields, editorConfig: BrandMarqueeConfig },
    Video: { fields: VideoFields, editorConfig: VideoConfig },
    EmergencyBand: { fields: EmergencyBandFields, editorConfig: EmergencyBandConfig },

    // Atomic primitives: the pieces to build a layout the opinionated sections
    // Don't cover. Freeform building is the advanced-editor tier, mirroring the
    // Form builder's standard-free / custom-paid line.
    HeadingBlock: {
      fields: HeadingBlockFields,
      editorConfig: HeadingBlockConfig,
      requiredFeature: "advanced_layout_editing",
    },
    TextBlock: {
      fields: TextBlockFields,
      editorConfig: TextBlockConfig,
      requiredFeature: "advanced_layout_editing",
    },
    ButtonBlock: {
      fields: ButtonBlockFields,
      editorConfig: ButtonBlockConfig,
      requiredFeature: "advanced_layout_editing",
    },
    ImageBlock: {
      fields: ImageBlockFields,
      editorConfig: ImageBlockConfig,
      requiredFeature: "advanced_layout_editing",
    },
    SpacerBlock: {
      fields: SpacerBlockFields,
      editorConfig: SpacerBlockConfig,
      requiredFeature: "advanced_layout_editing",
    },
    DividerBlock: {
      fields: DividerBlockFields,
      editorConfig: DividerBlockConfig,
      requiredFeature: "advanced_layout_editing",
    },
    SectionBlock: {
      fields: SectionBlockFields,
      editorConfig: SectionBlockConfig,
      requiredFeature: "advanced_layout_editing",
    },
    BadgeBlock: {
      fields: BadgeBlockFields,
      editorConfig: BadgeBlockConfig,
      requiredFeature: "advanced_layout_editing",
    },
    GroupBlock: {
      fields: GroupBlockFields,
      editorConfig: GroupBlockConfig,
      requiredFeature: "advanced_layout_editing",
    },
  } as const satisfies RegisteredJobsiteComponentMap,

  /** Feature slug a component needs before it can be added to a page, or null. */
  componentRequiredFeature = (type: string): string | null => {
    const entry = (JobsiteComponentRegistry as RegisteredJobsiteComponentMap)[type];
    return entry?.requiredFeature ?? null;
  },

  /**
   * Backward-compatible field registry used by AI harnesses and schema extraction.
   */
  ComponentRegistry = buildFieldRegistry(JobsiteComponentRegistry),

  /**
   * Backward-compatible alias consumed by hydration and generation code.
   */
  AllComponentFields = ComponentRegistry,

  /**
   * Puck editor components derived from the authoritative registry.
   * Utility-only editor components like ComponentRef stay separate.
   */
  PageEditorComponentRegistry = buildEditorComponentRegistry(
    JobsiteComponentRegistry,
  ) as Config<JobsitePageComponentProps, Record<string, never>>["components"];

type ComponentName = keyof typeof ComponentRegistry;
type JobsiteComponentName = keyof typeof JobsiteComponentRegistry;

export type { JobsitePageComponentProps, ComponentName, JobsiteComponentName };
export {
  JobsiteComponentRegistry,
  componentRequiredFeature,
  ComponentRegistry,
  AllComponentFields,
  PageEditorComponentRegistry,
};
